'use client';

import { useCallback, useEffect, useState, useTransition } from 'react';
import { createPortal } from 'react-dom';

import { useRouter } from 'next/navigation';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Bell, Building2, FileText, MapPin, Search, ShieldCheck } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useForm, useWatch } from 'react-hook-form';

import { formatPropertyAddress } from '@addava/skills/presentation';
import { Brand } from '~/components/brand';
import { Button } from '~/components/ui/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '~/components/ui/combobox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/components/ui/form';
import { Spinner } from '~/components/ui/spinner';
import { Trans } from '~/components/ui/trans';
import { cn } from '~/components/ui/utils';

import { AddressResultsSchema } from '~/lib/onboarding/schema';
import { parcelPreviewPath } from '~/lib/parcel-watch/parcel-share';

import { LookupSchema } from './_lib/schemas/lookup.schema';
import { PropertyNavigationPending } from './property-navigation-pending';

export interface PropertyAddress {
  address: string;
  block: string;
  lot: string;
  parcelAddress?: string;
}

/** Debounced city address search shared by every lookup surface. */
export function useAddressSearch(query: string, selected: unknown) {
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const searchText = query.trim().replace(/\s+/g, ' ').toUpperCase();
  const canSearch = searchText.length >= 3 && !selected;
  const results = useQuery({
    queryKey: ['property-addresses', searchText],
    enabled: canSearch && debouncedQuery === searchText,
    queryFn: async ({ signal }) => {
      const response = await fetch(
        `/api/parcel-watch/addresses?q=${encodeURIComponent(searchText)}`,
        { signal },
      );
      if (!response.ok) throw new Error('Address lookup failed');
      return AddressResultsSchema.parse(await response.json()).addresses;
    },
    staleTime: (query) => (query.state.data?.length === 0 ? 30_000 : 300_000),
    gcTime: 600_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
  // Debounce network reads; React Query immediately reuses exact cached queries
  // and cancels obsolete requests when the key changes. Never reuse another
  // query's suggestions while the user is entering a different address.
  useEffect(() => {
    if (!canSearch) return;
    const timer = setTimeout(() => setDebouncedQuery(searchText), 400);
    return () => clearTimeout(timer);
  }, [searchText, canSearch]);
  return {
    addresses: canSearch ? (results.data ?? []) : [],
    loading:
      canSearch &&
      !results.data?.length &&
      ((!results.data && debouncedQuery !== searchText) || results.isPending || results.isFetching),
    error: canSearch && debouncedQuery === searchText && results.isError,
  };
}

export function PropertyLookup(props: {
  locale: string;
  compact?: boolean;
  initialAddress?: string;
  homepage?: boolean;
  onSelect?: (address: PropertyAddress) => void;
}) {
  const t = useTranslations('parcelWatch');
  const router = useRouter();
  const [opening, startNavigation] = useTransition();
  const form = useForm({
    resolver: zodResolver(LookupSchema),
    defaultValues: { address: props.initialAddress ?? '' },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });
  const query = useWatch({ control: form.control, name: 'address' });
  const [state, setState] = useState({
    selected: null as PropertyAddress | null,
    open: Boolean(props.initialAddress),
  });
  const { addresses, loading, error } = useAddressSearch(query, state.selected);
  // A chosen homepage address opens its property page behind the page's own
  // skeleton, as the demo search does, rather than a blank route handoff.
  const [handoff, setHandoff] = useState<string>();
  const restoreHandoff = useCallback(() => setHandoff(undefined), []);
  const submit = (choice = state.selected) => {
    if (opening || handoff) return;
    if (props.homepage) {
      if (choice) {
        setHandoff(parcelPreviewPath(choice.block, choice.lot, props.locale, choice.address));
        return;
      }
      router.push(`/demo?${new URLSearchParams({ address: query.trim() })}`);
      return;
    }
    // Enter or the button opens the only match; several matches still need a choice.
    const selected = choice ?? (addresses.length === 1 ? addresses[0] : null);
    if (!selected) {
      form.setError('address', { message: 'parcelWatch.lookup.selectAddress' });
      return;
    }
    if (props.onSelect) {
      props.onSelect(selected);
      return;
    }
    startNavigation(() => {
      router.push(parcelPreviewPath(selected.block, selected.lot, props.locale, selected.address));
    });
  };

  return (
    <div
      className={cn(
        'pw-lookup',
        props.compact && 'pw-lookup-compact',
        props.homepage && 'pw-lookup-home',
      )}
    >
      {handoff &&
        createPortal(
          <div className="pw-property-handoff">
            <header className="mx-auto flex h-16 max-w-7xl items-center gap-8 px-4">
              <Brand />
            </header>
            <PropertyNavigationPending
              target={handoff}
              locale={props.locale}
              onRestore={restoreHandoff}
            />
          </div>,
          document.body,
        )}
      {!props.compact && (
        <div className="pw-lookup-copy">
          <h1>
            <Trans i18nKey="parcelWatch.lookup.title" />
            <span>
              <Trans i18nKey="parcelWatch.lookup.titleAccent" />
            </span>
          </h1>
          <p className="pw-intro">
            <Trans i18nKey="parcelWatch.lookup.description" />
          </p>
        </div>
      )}
      <div className="pw-lookup-box">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(() => submit())}
            noValidate
            className="grid gap-3"
            data-test="property-lookup-form"
          >
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {props.homepage ? (
                      'San Francisco address'
                    ) : (
                      <Trans i18nKey="parcelWatch.lookup.addressLabel" />
                    )}
                  </FormLabel>
                  <Combobox
                    open={state.open}
                    items={addresses}
                    value={state.selected}
                    filter={null}
                    itemToStringLabel={(item: PropertyAddress) =>
                      formatPropertyAddress(item.address)
                    }
                    isItemEqualToValue={(item: PropertyAddress, value: PropertyAddress) =>
                      item.block === value.block && item.lot === value.lot
                    }
                    inputValue={field.value}
                    onOpenChange={(open) => setState((current) => ({ ...current, open }))}
                    onInputValueChange={(value, details) => {
                      if (details.reason === 'input-clear' || details.reason === 'escape-key') {
                        details.cancel();
                        return;
                      }
                      if (details.reason === 'input-change') {
                        field.onChange(value);
                        setState((current) => ({
                          ...current,
                          selected: null,
                        }));
                      }
                    }}
                    onValueChange={(value: PropertyAddress | null) => {
                      if (opening) return;
                      setState((current) => ({
                        ...current,
                        selected: value,
                      }));
                      if (value) {
                        field.onChange(formatPropertyAddress(value.address));
                        form.clearErrors('address');
                        // Use the chosen result directly: React state has not
                        // committed yet when the combobox selection fires.
                        void form.handleSubmit(() => submit(value))();
                      }
                    }}
                  >
                    <FormControl>
                      <ComboboxInput
                        className="field-spotlight h-12 w-full"
                        placeholder={t('lookup.placeholder')}
                        showTrigger={false}
                        autoComplete="off"
                        autoFocus={Boolean(props.initialAddress)}
                        onKeyDownCapture={(event) => {
                          if (event.key === 'Escape') {
                            event.preventDefault();
                            event.stopPropagation();
                            setState((current) => ({ ...current, open: false }));
                          }
                        }}
                        onBlur={field.onBlur}
                        data-test="property-address-input"
                      >
                        <Search className="text-muted-foreground mr-3 size-4" aria-hidden="true" />
                      </ComboboxInput>
                    </FormControl>
                    <ComboboxContent>
                      <ComboboxEmpty className="p-4" data-test="property-address-feedback">
                        <Trans
                          i18nKey={
                            loading
                              ? 'parcelWatch.lookup.searching'
                              : error
                                ? 'parcelWatch.lookup.error'
                                : query.trim().length < 3
                                  ? 'parcelWatch.lookup.enterAddress'
                                  : 'parcelWatch.lookup.noResults'
                          }
                        />
                      </ComboboxEmpty>
                      <ComboboxList>
                        {(item: PropertyAddress) => (
                          <ComboboxItem
                            key={`${item.block}-${item.lot}-${item.address}`}
                            value={item}
                            className="gap-3 p-3"
                            data-test="property-address-option"
                          >
                            <MapPin className="text-primary size-4" />
                            <span>
                              <strong className="block font-semibold">
                                {formatPropertyAddress(item.address)}
                              </strong>
                              <small className="text-muted-foreground">
                                <Trans i18nKey="parcelWatch.common.city" /> · {item.block}/
                                {item.lot}
                              </small>
                              {item.parcelAddress && (
                                <small className="text-muted-foreground block">
                                  <Trans
                                    i18nKey="parcelWatch.lookup.parcelAddress"
                                    values={{ address: formatPropertyAddress(item.parcelAddress) }}
                                  />
                                </small>
                              )}
                            </span>
                          </ComboboxItem>
                        )}
                      </ComboboxList>
                    </ComboboxContent>
                  </Combobox>
                  {query.length > 0 && (
                    <FormDescription>
                      <Trans i18nKey="parcelWatch.lookup.addressHelp" />
                    </FormDescription>
                  )}
                  {/* The open list announces errors itself; keep one visible once it closes. */}
                  {error && !state.open && (
                    <p className="text-destructive text-sm" role="alert">
                      <Trans i18nKey="parcelWatch.lookup.error" />
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              size="marketing"
              className="btn-primary w-full gap-2"
              type="submit"
              disabled={opening}
              data-test={props.homepage ? 'property-demo-cta' : 'check-property-button'}
            >
              {opening ? (
                <>
                  <Spinner className="size-4" />
                  <Trans i18nKey="parcelWatch.lookup.openingProperty" />
                </>
              ) : (
                <Trans
                  i18nKey={
                    props.compact && props.onSelect
                      ? 'parcelWatch.lookup.selectProperty'
                      : 'parcelWatch.lookup.checkProperty'
                  }
                />
              )}
              <ArrowRight className="size-4" />
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}

export function PropertyLookupBenefits() {
  return (
    <aside className="pw-benefits">
      <div className="pw-benefit-heading">
        <span className="pw-property-icon">
          <Building2 className="size-7" />
        </span>
        <div>
          <h2>
            <Trans i18nKey="parcelWatch.lookup.benefitsTitle" />
          </h2>
        </div>
      </div>
      {[
        {
          Icon: FileText,
          title: 'permitsTitle',
          description: 'permitsDescription',
        },
        {
          Icon: ShieldCheck,
          title: 'safetyTitle',
          description: 'safetyDescription',
        },
        {
          Icon: Bell,
          title: 'monitorTitle',
          description: 'monitorDescription',
        },
      ].map(({ Icon, title, description }) => (
        <div className="pw-benefit-row" key={title}>
          <Icon className="text-primary size-5" />
          <div>
            <h3>
              <Trans i18nKey={`parcelWatch.lookup.${title}`} />
            </h3>
            <p>
              <Trans i18nKey={`parcelWatch.lookup.${description}`} />
            </p>
          </div>
        </div>
      ))}
      <p className="pw-benefits-foot">
        <Trans i18nKey="parcelWatch.lookup.benefitsFoot" />
      </p>
    </aside>
  );
}
